<script>
import Layout from '@layouts/full.vue'
import { authMethods } from '@state/helpers'
import appConfig from '@src/app.config'

export default {
  page: {
    title: 'Log in',
    meta: [{ name: 'description', content: `Log in to ${appConfig.title}` }],
  },
  components: { Layout },
  data() {
    return {
      email: '',
      password: '',
      deviceName: 'Browser',
      authError: null,
      tryingToLogIn: false,
      pageTitle: appConfig.title,
    }
  },
  methods: {
    ...authMethods,
    // Try to log the user in with the username
    // and password they provided.
    tryToLogIn() {
      this.tryingToLogIn = true
      // Reset the authError if it existed.
      this.authError = null
      return this.logIn({
        email: this.email,
        password: this.password,
        deviceName: this.deviceName,
      })
        .then((token) => {
          this.tryingToLogIn = false;

          // Redirect to the originally requested page, or to the home page
          this.$router.push(this.$route.query.redirectFrom || { name: 'home' })
        })
        .catch((error) => {
          this.tryingToLogIn = false
          this.authError = error
        })
    },
  },
}
</script>

<template>
  <Layout>
    <form :class="$style.form" @submit.prevent="tryToLogIn">
      <img
        :class="$style.logo"
        src="@assets/images/logo.png"
        alt="Kooi Security"
      />
      <header :class="$style.header">
        <div :class="$style.headerLeft">
          <BaseCircle
            ><svg width="24" height="24" viewBox="0 0 17 23" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M2.905 13.036a16.46 16.46 0 0111.183-.003c1.697.631 3.025 2.623 2.904 4.354l-.313 4.686a.989.989 0 01-.982.927H1.307a.989.989 0 01-.982-.925l-.318-4.691c-.114-1.728 1.214-3.719 2.898-4.348zm10.511 1.866a14.544 14.544 0 00-9.839.002c-.948.355-1.657 1.574-1.607 2.346l.254 3.762h12.553l.253-3.762c.054-.774-.654-1.991-1.614-2.348zM8.583 0c2.602 0 4.72 2.398 4.72 5.347 0 2.948-2.118 5.346-4.72 5.346-2.603 0-4.72-2.398-4.72-5.346C3.863 2.398 5.98 0 8.583 0zm0 1.988c-1.518 0-2.753 1.507-2.753 3.36 0 1.85 1.235 3.357 2.753 3.357 1.517 0 2.753-1.506 2.753-3.358s-1.236-3.36-2.753-3.36z" fill="currentColor"/></svg></BaseCircle>
        </div>
        <div :class="$style.headerRight">
          <h1>{{ pageTitle }}</h1>
          <h2>Login</h2>
        </div>
      </header>
      <BaseInputText
        v-model="email"
        name="email"
        type="email"
        label="Email"
        focus
      />
      <BaseInputText
        v-model="password"
        name="password"
        type="password"
        label="Password"
      />
      <BaseButton :disabled="tryingToLogIn" type="submit" button-style="icon">
        <span v-if="tryingToLogIn">
          please wait
        </span>
        <span v-else>
          login
        </span>
        <svg width="24" height="24" viewBox="0 0 7 12" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M1.747.301A1.019 1.019 0 00.3.301a1.03 1.03 0 000 1.453L4.53 6 .3 10.246A1.03 1.03 0 001.023 12c.262 0 .524-.1.724-.301L6.7 6.726a1.033 1.033 0 000-1.452L1.747.3z" fill="currentColor"/></svg>
      </BaseButton>
      <BaseMessage v-if="authError"
        >Wrong e-mail address or password.</BaseMessage
      >
      <hr />
      <footer :class="$style.footer">
        <p
          >Lost your login or password?
          <BaseLink href="/forgot-password"
            ><strong>Click here.</strong></BaseLink
          ></p
        >
      </footer>
    </form>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.form {
  width: 100%;
  max-width: 100%;
  padding: 30px;
  margin: 0;
  background: $color-block-bg;
  border-radius: 5px;

  @media (min-width: $breakpoint-sm) {
    max-width: 458px;
    padding: 51px 46px 39px 47px;
    margin: 50px auto 0 auto;
  }

  input {
    width: 100%;
  }

  label {
    max-width: 168px;

    &:first-of-type {
      max-width: 342px;
    }
  }

  button {
    margin: 20px 0 0 0;
  }

  hr {
    margin: 60px 0 0;
    border-top: 1px solid $color-hr-border;
    opacity: 0.28;
  }
}

.logo {
  max-width: 127px;
  margin: 0 0 54px 4px;
}

.header {
  display: flex;
  align-items: center;
  margin-bottom: 42px;

  .headerLeft {
    margin-right: 12px;
  }

  .headerRight {
    h1,
    h2 {
      margin: 0;
      font-size: 25px;
      line-height: 1.08;
      letter-spacing: -0.86px;
    }

    h1 {
      font-weight: 400;
    }
  }
}

.footer {
  color: transparentize($color-text, 0.3);
  letter-spacing: -0.1px;

  @media (max-width: 640px) {
    font-size: 14px;
  }

  a {
    color: transparentize($color-text, 0.3);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
